import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { isValidPhoneNumber } from 'libphonenumber-js';

import {
  HomeContainer,
  Content,
  Card,
  Button,
  Input,
  HeaderTitle,
  Row,
  Title,
  Column,
  Subtitle,
  CardWrapper,
  LeftSide,
  RightSide,
  TextArea,
  CardRow,
  Name,
  RadioContainer,
  RadioWrapper,
  UploadText,
  UploadContainer,
  Image,
  AddButton,
  TitleContainer,
  Delete,
  Buttons,
  SelectStyled,
} from './CreateDeliveryPage.styles.js';

import Footer from '../../components/Footer/Footer.js';
import AppLoader from '../../components/utils/AppLoader.js';
import PhoneNumberInput from '../../components/PhoneNumberInput/PhoneNumberInput';

import openToast from '../../utils/toast.js';
import { selectedLocale } from '../../store/slices/common/slice.js';

import { GoogleMap, Marker, StandaloneSearchBox, useJsApiLoader } from '@react-google-maps/api';
import { DEFAULT_LATITUDE, DEFAULT_LONGITUDE, googlePlacesKey, MAP_LIBRARIES, MAP_ZOOM_LEVEL } from '../../utils/constants.js';

import { ReactComponent as Loading } from '../../assets/icons/loader-white.svg';
import Upload from '../../assets/images/upload.png';

import { selectCreateDeliveryPending } from '../../store/slices/delivery/slice.js';
import { createDelivery } from '../../store/slices/delivery/asyncThunks.js';
import { getDeliveryUsers } from '../../store/slices/user/asyncThunks.js';
import { selectDeliveryUsers, selectGetDeliveryUsersPending } from '../../store/slices/user/slice.js';
import _ from 'lodash';
import Sidebar from '../../components/Sidebar/Sidebar.js';

const paymentStatusData = [
  { id: 1, name: 'paid', value: 'paid' },
  { id: 2, name: 'not-paid', value: 'notPaid' },
];

const deliveryTypeData = [
  { id: 1, name: 'pick-up' },
  { id: 2, name: 'drop-off' },
];

const CreateDeliveryPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toastId = useRef(null);

  const locale = useSelector(selectedLocale);
  const ar = locale === 'ar';

  const loading = useSelector(selectCreateDeliveryPending);

  const [checkValidation, setCheckValidation] = useState(false);
  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [deliveryType, setDeliveryType] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [ygiiOrder, setYgiiOrder] = useState('');
  const [partnerOrder, setPartnerOrder] = useState('');
  const [addressDetails, setAddressDetails] = useState('');
  const [deliveryLocation, setDeliveryLocation] = useState('');
  const [items, setItems] = useState([{ id: 1, name: '', price: '', quantity: '', photo: null, fileName: '' }]);
  const deliveryUsers = useSelector(selectDeliveryUsers);
  const getDeliveryUsersPending = useSelector(selectGetDeliveryUsersPending);
  const [selectedDeliveryUser, setSelectedDeliveryUser] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    dispatch(getDeliveryUsers({ page: 1, search: '' }))
      .unwrap()
      .then(() => {})
      .catch(err => {
        openToast(toastId, 'login', 'error', t(err.message));
      });
  }, [dispatch, t]);

  const validateForm = () => {
    let formIsValid = true;
    if (!fullName) {
      openToast(toastId, 'create-delivery-error-fullName', 'error', t('full-name-is-required'));
      formIsValid = false;
      return;
    }

    if (!deliveryType) {
      openToast(toastId, 'create-delivery-error-deliveryType', 'error', t('delivery-type-is-required'));
      formIsValid = false;
      return;
    }

    if (!paymentStatus) {
      openToast(toastId, 'create-delivery-error-paymentStatus', 'error', t('payment-status-is-required'));
      formIsValid = false;
      return;
    }

    if (!deliveryLocation) {
      openToast(toastId, 'create-delivery-error-deliveryLocation', 'error', t('delivery-location-is-required'));
      formIsValid = false;
      return;
    }

    if (!addressDetails) {
      openToast(toastId, 'create-delivery-error-addressDetails', 'error', t('address-details-is-required'));
      formIsValid = false;
      return;
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (!item.name) {
        openToast(toastId, `create-delivery-error-itemsFileName-${i}`, 'error', t('item-details-is-required'));
        formIsValid = false;
        return;
      }
      if (!item.price) {
        openToast(toastId, `create-delivery-error-itemsPrice-${i}`, 'error', t('item-details-is-required'));
        formIsValid = false;
        return;
      }
      if (!item.quantity) {
        openToast(toastId, `create-delivery-error-itemsQuantity-${i}`, 'error', t('item-details-is-required'));
        formIsValid = false;
        return;
      }
      if (!item.fileName) {
        openToast(toastId, `create-delivery-error-itemsFileName-${i}`, 'error', t('item-details-is-required'));
        formIsValid = false;
        return;
      }
    }

    if (!phoneNumber) {
      openToast(toastId, 'create-delivery-error-phoneNumber', 'error', t('phone-number-is-required'));
      formIsValid = false;
      return;
    } else if (!isValidPhoneNumber(phoneNumber)) {
      openToast(toastId, 'create-delivery-error-isValid-phoneNumber', 'error', t('invalid-phone-number'));
      formIsValid = false;
      return;
    }

    return formIsValid;
  };

  const searchBoxRef = useRef();
  const mapRef = useRef();
  const [mapZoom, setMapZoom] = useState(MAP_ZOOM_LEVEL);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: googlePlacesKey,
    libraries: MAP_LIBRARIES,
  });

  const center =
    deliveryLocation?.lat && deliveryLocation?.lng
      ? {
          lat: deliveryLocation.lat,
          lng: deliveryLocation.lng,
        }
      : {
          lat: DEFAULT_LATITUDE,
          lng: DEFAULT_LONGITUDE,
        };

  const handlePlacesChanged = map => {
    const places = searchBoxRef.current?.getPlaces();

    if (places && places.length > 0) {
      const place = places[0];
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      const address = place.formatted_address;
      setDeliveryLocation({ lat, lng, name: address });

      if (mapRef.current) {
        mapRef.current.setCenter({ lat, lng });
        setMapZoom(14);
      }
    }
  };

  const handleMarkerDragEnd = event => {
    const { latLng } = event;
    const lat = latLng.lat();
    const lng = latLng.lng();
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === 'OK' && results?.length > 0) {
        const address = results[0].formatted_address;
        setDeliveryLocation({ lat, lng, name: address });
      }
    });
  };

  const handleMapClick = event => {
    const { latLng } = event;
    const lat = latLng.lat();
    const lng = latLng.lng();
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === 'OK' && results?.length > 0) {
        const address = results[0].formatted_address;
        setDeliveryLocation({ lat, lng, name: address });
      }
    });
  };

  const onLoad = useCallback(
    function callback(map) {
      mapRef.current = map;
      map.setCenter(center);
      map.setZoom(MAP_ZOOM_LEVEL);
    },
    [center],
  );

  const handleChange = (inputValue, setValue) => {
    if (/^[0-9]*$/.test(inputValue)) {
      setValue(inputValue);
    }
  };

  const handleAddItem = () => {
    setItems([
      ...items,
      {
        id: Date.now(), // Unique ID for each item
        name: '',
        price: '',
        quantity: '',
        photo: null,
        fileName: '',
      },
    ]);
  };

  const itemPhotoUploadRefs = useRef([]);

  // Remove item by index
  const handleRemoveItem = index => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);

    // Remove the corresponding ref
    itemPhotoUploadRefs.current = itemPhotoUploadRefs.current.filter((_, i) => i !== index);
  };

  // Initialize ref for each item dynamically
  const setRef = (index, ref) => {
    itemPhotoUploadRefs.current[index] = ref;
  };

  // Handle image upload and set file name
  const handleImageUpload = (e, index) => {
    const file = e.target.files[0];
    const updatedItems = [...items];
    updatedItems[index].photo = file;
    updatedItems[index].fileName = file ? file.name : '';
    setItems(updatedItems);
  };

  // Update input values dynamically
  const handleInputChange = (e, index, field, number) => {
    const updatedItems = [...items];
    const inputValue = e.target.value;

    if (number) {
      if (/^[0-9]*$/.test(inputValue)) {
        updatedItems[index][field] = inputValue;
        setItems(updatedItems);
      }
    } else {
      updatedItems[index][field] = inputValue;
      setItems(updatedItems);
    }
  };

  const resetData = () => {
    setItems([{ id: 1, name: '', price: '', quantity: '', photo: null, fileName: '' }]);
    setPaymentStatus('');
    setDeliveryType('');
    setPhoneNumber('');
    setFullName('');
    setYgiiOrder('');
    setPartnerOrder('');
    setAddressDetails('');
    setDeliveryLocation({ lat: '', lng: '', name: '' });
    setCheckValidation(false);
  };

  const onCreate = () => {
    setCheckValidation(true);
    if (validateForm()) {
      dispatch(
        createDelivery({
          paymentStatus,
          deliveryType,
          phone: phoneNumber.replaceAll(' ', ''),
          fullName,
          addressDetails,
          locationLat: deliveryLocation.lat,
          locationLng: deliveryLocation.lng,
          locationName: deliveryLocation.name,
          ...(items[0].name && { items }),
          ...(ygiiOrder && { ygiiOrderId: ygiiOrder }),
          ...(partnerOrder && { ygiiPartnerId: partnerOrder }),
        }),
      )
        .unwrap()
        .then(() => {
          openToast(toastId, 'create-delivery-success', 'success', t('delivery-has-been-created-successfully'));

          resetData();
          navigate('/logistics?page=1');
          window.scrollTo(0, 0);
        })
        .catch(err => openToast(toastId, 'create-delivery-error', 'error', t(err.message)));
    }
  };

  const searchFunction = (inputValue, { action }) => {
    if (action === 'input-change') {
      setSearch(inputValue);

      dispatch(getDeliveryUsers({ page: 1, search: inputValue }))
        .unwrap()
        .catch(err => openToast(toastId, 'login', 'error', t(err.message)));
    }
  };

  const fetchNextPage = () => {
    if (!deliveryUsers.maxReached && !getDeliveryUsersPending) {
      dispatch(getDeliveryUsers({ page: deliveryUsers.page + 1, search }))
        .unwrap()
        .catch(err => openToast(toastId, 'login', 'error', t(err.message)));
    }
  };
  const handleChangeDeliveryUser = option => {
    if (option) {
      setSelectedDeliveryUser(option);
      setFullName(option.fullName ? option.fullName : '');
      setPhoneNumber(option.phone ? option.phone : '');
      if (option.lat && option.lng && option.addressName) {
        setDeliveryLocation({
          lat: option.lat,
          lng: option.lng,
          name: option.addressName,
        });
      }

      setAddressDetails(option.addressDetails ? option.addressDetails : '');
    } else {
      setSelectedDeliveryUser({});
      setFullName('');
      setPhoneNumber('');
      setDeliveryLocation({ lat: '', lng: '', name: '' });
      setAddressDetails('');
      dispatch(getDeliveryUsers({ page: 1, search: '' }))
        .unwrap()
        .catch(err => openToast(toastId, 'login', 'error', t(err.message)));
    }
  };

  return (
    <HomeContainer>
      <Sidebar />
      {/* <HeaderWrapper>
        <Header />
      </HeaderWrapper> */}

      <Content>
        <Card>
          <HeaderTitle>{t('delivery')}</HeaderTitle>
          <CardWrapper>
            <Row style={{ maxWidth: 450 }}>
              <Title>{t('payment-status')}</Title>
              <RadioContainer>
                {paymentStatusData.map((status, index) => (
                  <RadioWrapper
                    key={index}
                    onClick={() => {
                      if (paymentStatus !== status.value) {
                        setPaymentStatus(status.value);
                      } else {
                        setPaymentStatus('');
                      }
                    }}>
                    <input
                      type="radio"
                      name="paymentStatus"
                      value={status.value}
                      checked={paymentStatus === status.value}
                      onChange={e => {
                        setPaymentStatus(e.target.value);
                      }}
                    />
                    <Name>{t(status.name)}</Name>
                  </RadioWrapper>
                ))}
              </RadioContainer>
            </Row>
            <Row style={{ maxWidth: 450 }}>
              <Title>{t('delivery-type')}</Title>
              <RadioContainer>
                {deliveryTypeData.map((type, index) => (
                  <RadioWrapper
                    key={index}
                    onClick={() => {
                      if (deliveryType !== type.name) {
                        setDeliveryType(type.name);
                      } else {
                        setDeliveryType('');
                      }
                    }}>
                    <input
                      type="radio"
                      name="deliveryType"
                      value={type.name}
                      checked={deliveryType === type.name}
                      onChange={e => {
                        setDeliveryType(e.target.value);
                      }}
                    />
                    <Name>{t(type.name)}</Name>
                  </RadioWrapper>
                ))}
              </RadioContainer>
            </Row>

            <CardRow>
              <LeftSide>
                <Column>
                  <Title>{t('contact-details')}</Title>
                  <SelectStyled
                    options={deliveryUsers.data}
                    placeholder={t('full-name')}
                    $error={checkValidation && !fullName}
                    value={selectedDeliveryUser}
                    onChange={handleChangeDeliveryUser}
                    getOptionLabel={e => e.fullName}
                    getOptionValue={e => e.id}
                    onMenuScrollToBottom={() => fetchNextPage()}
                    isLoading={getDeliveryUsersPending}
                    isClearable
                    onInputChange={searchFunction}
                    styles={{
                      control: base => ({
                        ...base,
                        border: 'none',
                        boxShadow: 'none',
                        marginTop: -5,
                      }),
                      menu: base => ({
                        ...base,

                        border: 'none',
                        left: 0,
                      }),
                    }}
                  />
                  {/* <Input
                    $ar={ar}
                    type={"text"}
                    placeholder={t("full-name")}
                    $error={checkValidation && !fullName}
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                  /> */}
                  <PhoneNumberInput phone={phoneNumber ?? ''} setPhone={setPhoneNumber} checkValidation={checkValidation} />
                </Column>

                <Column style={{ marginTop: 20 }}>
                  <Title>{t('order-details')}</Title>
                  <Row>
                    <Subtitle>{t('ygii-order')}</Subtitle>
                    <Input
                      $small
                      $ar={ar}
                      type={'text'}
                      placeholder={t('ygii-order')}
                      value={ygiiOrder}
                      onChange={e => handleChange(e.target.value, setYgiiOrder)}
                    />
                  </Row>
                  <Row>
                    <Subtitle>{t('partner-order')}</Subtitle>
                    <Input
                      $small
                      $ar={ar}
                      type={'text'}
                      placeholder={t('partner-order')}
                      value={partnerOrder}
                      onChange={e => handleChange(e.target.value, setPartnerOrder)}
                    />
                  </Row>
                </Column>

                {items.map((item, index) => (
                  <Column key={item.id}>
                    <TitleContainer>
                      <Title>{t('item-details')}</Title>
                      {index > 0 && <Delete onClick={() => handleRemoveItem(index)} />}
                    </TitleContainer>
                    <Row>
                      <Subtitle>{t('item-name')}</Subtitle>
                      <Input
                        $small
                        $ar={ar}
                        type={'text'}
                        placeholder={t('item-name')}
                        value={item.name}
                        onChange={e => handleInputChange(e, index, 'name', false)}
                      />
                    </Row>
                    <Row>
                      <Subtitle>{t('quantity')}</Subtitle>
                      <Input
                        $small
                        $ar={ar}
                        type={'text'}
                        placeholder={t('quantity')}
                        $error={checkValidation && !item.quantity && item.name}
                        value={item.quantity}
                        onChange={e => handleInputChange(e, index, 'quantity', true)}
                      />
                    </Row>
                    <Row>
                      <Subtitle>{t('item-price')}</Subtitle>
                      <Input
                        $small
                        $ar={ar}
                        type={'text'}
                        placeholder={t('item-price')}
                        $error={checkValidation && !item.price && item.name}
                        value={item.price}
                        onChange={e => handleInputChange(e, index, 'price', true)}
                      />
                    </Row>

                    <Row>
                      <Subtitle>{t('item-photo')}</Subtitle>
                      {item.fileName ? (
                        <UploadContainer
                          onClick={() => {
                            const updatedItems = [...items];
                            updatedItems[index].photo = '';
                            updatedItems[index].fileName = '';

                            setItems(updatedItems);
                          }}>
                          <UploadText $red>{item.fileName}</UploadText>
                        </UploadContainer>
                      ) : (
                        <UploadContainer
                          onClick={() => {
                            itemPhotoUploadRefs.current[index]?.click();
                          }}>
                          <UploadText>{t('upload')}</UploadText>
                          <Image src={Upload} />
                          <input
                            ref={ref => setRef(index, ref)}
                            accept="image/*"
                            type="file"
                            style={{ display: 'none' }}
                            onChange={e => handleImageUpload(e, index)}
                          />
                        </UploadContainer>
                      )}
                    </Row>
                  </Column>
                ))}

                <Row onClick={handleAddItem}>
                  <Subtitle>{t('add-another-item')}</Subtitle>
                  <UploadContainer>
                    <AddButton>+</AddButton>
                  </UploadContainer>
                </Row>
              </LeftSide>

              <RightSide>
                <Column>
                  <Title>{t('delivery-location')}</Title>
                </Column>
                {isLoaded ? (
                  <>
                    <StandaloneSearchBox
                      onLoad={ref => {
                        searchBoxRef.current = ref;
                      }}
                      onPlacesChanged={handlePlacesChanged}>
                      <Input
                        type="text"
                        placeholder={t('search-for-location')}
                        value={deliveryLocation?.name}
                        $error={checkValidation && !deliveryLocation.name}
                        onChange={e => {
                          setDeliveryLocation(prevLocation => ({
                            ...prevLocation,
                            name: e.target.value,
                          }));
                        }}
                      />
                    </StandaloneSearchBox>
                    <GoogleMap
                      mapContainerClassName="map"
                      mapContainerStyle={{
                        border: '1px solid white',
                        width: '100%',
                        maxWidth: 400,
                        height: 400,
                        borderRadius: 10,
                        boxShadow: 'rgba(50, 50, 93, 0.25) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px',
                      }}
                      onLoad={map => {
                        mapRef.current = map;
                        onLoad(map);
                      }}
                      zoom={mapZoom}
                      onClick={handleMapClick}>
                      <Marker
                        position={
                          deliveryLocation?.lat && deliveryLocation?.lng
                            ? {
                                lat: deliveryLocation.lat,
                                lng: deliveryLocation.lng,
                              }
                            : {
                                lat: DEFAULT_LATITUDE,
                                lng: DEFAULT_LONGITUDE,
                              }
                        }
                        draggable
                        onDragEnd={handleMarkerDragEnd}
                      />
                    </GoogleMap>
                  </>
                ) : (
                  <div style={{ marginTop: 50 }}>
                    <Loading />
                  </div>
                )}
                <Column style={{ marginTop: 20 }}>
                  <Title>{t('address-details')}</Title>
                  <TextArea
                    $ar={ar}
                    type={'text'}
                    placeholder={t('building-number')}
                    $error={checkValidation && !addressDetails}
                    value={addressDetails}
                    onChange={e => setAddressDetails(e.target.value)}
                  />
                </Column>
              </RightSide>
            </CardRow>
          </CardWrapper>
          <Buttons>
            <Button
              onClick={() => {
                if (!loading) {
                  navigate('/logistics?page=1');
                }
              }}>
              {t('back')}
            </Button>
            {loading ? (
              <Button disabled>
                <AppLoader size={24} borderTop={'3px solid #484888'} />
              </Button>
            ) : (
              <Button onClick={onCreate}>{t('create')}</Button>
            )}
          </Buttons>
        </Card>
      </Content>

      <Footer ar={ar} />
    </HomeContainer>
  );
};

export default CreateDeliveryPage;
